/**
 * Gráficos da Fase 4 em SVG próprio (sem dependência de runtime).
 *
 * Regras seguidas (docs/12-metricas.md, seção "Gráficos"):
 * - Um eixo por gráfico; nunca dois eixos Y.
 * - Barras finas (≤ 24 px) com ponta arredondada de 4 px e base reta; linhas de 2 px; pontos ≥ 8 px com anel
 *   de 2 px na cor da superfície; grade em linha fina e contínua.
 * - Cor por função: série principal em azul da marca; referência (planejado) em cinza; intensidade numa
 *   rampa ordinal de um só tom (validada em claro e escuro). Texto nunca usa a cor da série.
 * - Camada de hover/foco em todo gráfico, e uma tabela com os mesmos números ("Ver tabela").
 * - Dado ausente não vira zero: a barra simplesmente não aparece e a tabela mostra "—".
 */
import { svgScaleStyle, useSvgScale } from "../lib/useSvgScale";
import { useId, useMemo, useState, type ReactNode } from "react";
import { t, LOCALE } from "../i18n";

const W = 640;
const H = 200;
const PAD = { l: 48, r: 12, t: 14, b: 28 };
/** Margem esquerda que acompanha o tamanho do texto do eixo Y (que cresce com `k`): o rótulo nunca sai da área do gráfico. */
const leftPad = (k: number, labels: string[]) =>
  Math.min(160, Math.max(PAD.l, Math.ceil(Math.max(0, ...labels.map((l) => l.length)) * 7.4 * k) + 12));
const PLOT_H = H - PAD.t - PAD.b;

export function niceTicks(min: number, max: number, count = 4): number[] {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(+v.toFixed(6));
  return out;
}

interface TipState {
  xPct: number;
  yPct: number;
  title: string;
  lines: { key: string; label: string; value: string; swatch?: string; line?: boolean }[];
}

function Tooltip({ tip }: { tip: TipState | null }) {
  if (!tip) return null;
  const right = tip.xPct > 60;
  return (
    <div
      className="chart-tip"
      role="status"
      style={{ left: `${tip.xPct}%`, top: `${Math.max(4, tip.yPct)}%`, transform: right ? "translate(calc(-100% - 12px), -50%)" : "translate(12px, -50%)" }}
    >
      <div className="chart-tip-title">{tip.title}</div>
      {tip.lines.map((l) => (
        <div className="chart-tip-row" key={l.key}>
          {l.swatch && <span className={l.line ? "key-line" : "key-box"} style={{ background: l.swatch }} aria-hidden />}
          <strong>{l.value}</strong>
          <span>{l.label}</span>
        </div>
      ))}
    </div>
  );
}

export interface LegendItem {
  label: string;
  color: string;
  kind?: "box" | "line" | "dot";
}

function Legend({ items }: { items: LegendItem[] }) {
  if (items.length < 2) return null;
  return (
    <ul className="chart-legend">
      {items.map((i) => (
        <li key={i.label}>
          <span className={i.kind === "line" ? "key-line" : i.kind === "dot" ? "key-dot" : "key-box"} style={{ background: i.color }} aria-hidden />
          {i.label}
        </li>
      ))}
    </ul>
  );
}

export function ChartFrame({
  title,
  subtitle,
  legend,
  table,
  children,
  empty,
}: {
  title: string;
  subtitle?: ReactNode;
  legend?: LegendItem[];
  table: { head: string[]; rows: (string | number)[][] };
  children: ReactNode;
  empty?: string | null;
}) {
  const id = useId();
  return (
    <figure className="chart-card" aria-labelledby={`${id}-t`}>
      <div className="chart-head">
        <figcaption id={`${id}-t`}>{title}</figcaption>
        {subtitle && <p className="chart-sub">{subtitle}</p>}
      </div>
      {empty ? (
        <p className="chart-empty">{empty}</p>
      ) : (
        <>
          {legend && <Legend items={legend} />}
          {children}
          <details className="chart-table">
            <summary>{t("Ver tabela")}</summary>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    {table.head.map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((r, i) => (
                    <tr key={i}>
                      {r.map((c, j) => (
                        <td key={j} className={j > 0 ? "num" : undefined}>
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </figure>
  );
}

function YGrid({ ticks, sy, fmt, left = PAD.l }: { ticks: number[]; sy: (v: number) => number; fmt: (v: number) => string; left?: number }) {
  return (
    <>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={left} x2={W - PAD.r} y1={sy(v)} y2={sy(v)} className="grid" />
          <text x={left - 6} y={sy(v) + 4} textAnchor="end" className="tick">
            {fmt(v)}
          </text>
        </g>
      ))}
    </>
  );
}

/** Caminho de barra com ponta arredondada (4 px) e base reta. */
function barPath(x: number, w: number, yTop: number, yBase: number): string {
  const h = yBase - yTop;
  if (h <= 0) return "";
  const r = Math.min(4, w / 2, h);
  return `M${x},${yBase}V${yTop + r}Q${x},${yTop} ${x + r},${yTop}H${x + w - r}Q${x + w},${yTop} ${x + w},${yTop + r}V${yBase}Z`;
}

// ---------------------------------------------------------------------------- barras (com referência)

export interface BarDatum {
  key: string;
  label: string; // rótulo do eixo X
  title: string; // título do tooltip
  value: number | null;
  ref?: number | null; // valor de referência (ex.: planejado), desenhado como traço
  extra?: string; // linha adicional no tooltip
}

export function BarChart({
  data,
  fmt,
  axisFmt,
  valueLabel,
  refLabel,
  labelEvery = 1,
}: {
  data: BarDatum[];
  fmt: (v: number) => string;
  axisFmt?: (v: number) => string;
  valueLabel: string;
  refLabel?: string;
  labelEvery?: number;
}) {
  const [svgRef, k] = useSvgScale(W);   // textos com 12px na tela mesmo com o gráfico encolhido
  const [tip, setTip] = useState<TipState | null>(null);
  const max = Math.max(1, ...data.map((d) => Math.max(d.value ?? 0, d.ref ?? 0)));
  const ticks = niceTicks(0, max, 4);
  const L = leftPad(k, ticks.map((v) => (axisFmt ?? fmt)(v)));
  const plotW = W - L - PAD.r;
  const top = Math.max(max, ticks[ticks.length - 1] ?? max);
  // com a fonte compensada (k), o rótulo da barra mais alta precisa de mais espaço no topo e as datas, de mais espaço
  const padT = PAD.t + Math.round(14 * (k - 1));
  const padB = PAD.b + Math.round(12 * (k - 1));   // o rótulo do eixo X cresce com k: sem isso encosta no "0" do eixo Y
  const plotH = H - padT - padB;
  const sy = (v: number) => padT + plotH - (v / top) * plotH;
  const every = Math.max(labelEvery, Math.ceil((data.length * 40 * k) / plotW));
  const slot = plotW / Math.max(1, data.length);
  const bw = Math.min(24, slot * 0.6);
  const maxIdx = data.reduce((best, d, i) => ((d.value ?? -1) > (data[best]?.value ?? -1) ? i : best), 0);
  let lastIdx = -1;
  data.forEach((d, i) => {
    if (d.value != null && d.value > 0) lastIdx = i;
  });
  const show = (i: number) => {
    const d = data[i];
    const cx = L + slot * i + slot / 2;
    setTip({
      xPct: (cx / W) * 100,
      yPct: (sy(Math.max(d.value ?? 0, d.ref ?? 0)) / H) * 100,
      title: d.title,
      lines: [
        { key: "v", label: valueLabel, value: d.value == null ? t("sem dado") : fmt(d.value), swatch: "var(--chart-1)" },
        ...(refLabel && d.ref != null ? [{ key: "r", label: refLabel, value: fmt(d.ref), swatch: "var(--chart-ref)", line: true }] : []),
        ...(d.extra ? [{ key: "e", label: "", value: d.extra }] : []),
      ],
    });
  };
  return (
    <div className="chart-plot" onPointerLeave={() => setTip(null)}>
      <svg ref={svgRef} style={svgScaleStyle(k)} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={valueLabel}>
        <YGrid left={L} ticks={ticks} sy={sy} fmt={axisFmt ?? fmt} />
        {data.map((d, i) => {
          const x = L + slot * i + (slot - bw) / 2;
          const labelled = i === maxIdx || i === lastIdx;
          return (
            <g key={d.key}>
              {d.value != null && d.value > 0 && <path d={barPath(x, bw, sy(d.value), sy(0))} className="bar" />}
              {d.ref != null && d.ref > 0 && (
                <line x1={x - 3} x2={x + bw + 3} y1={sy(d.ref)} y2={sy(d.ref)} className="ref-tick" />
              )}
              {labelled && d.value != null && d.value > 0 && (
                <text x={x + bw / 2} y={sy(Math.max(d.value, d.ref ?? 0)) - 6} textAnchor="middle" className="val">
                  {fmt(d.value)}
                </text>
              )}
              {i % every === 0 && (
                <text x={L + slot * i + slot / 2} y={H - 8} textAnchor="middle" className="tick">
                  {d.label}
                </text>
              )}
              <rect
                x={L + slot * i}
                y={padT}
                width={slot}
                height={plotH}
                className="hit"
                tabIndex={0}
                aria-label={`${d.title}: ${d.value == null ? t("sem dado") : fmt(d.value)}`}
                onPointerMove={() => show(i)}
                onFocus={() => show(i)}
                onBlur={() => setTip(null)}
              />
            </g>
          );
        })}
        <line x1={L} x2={W - PAD.r} y1={sy(0)} y2={sy(0)} className="axis" />
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
}

// ---------------------------------------------------------------------------- barras empilhadas (intensidade)

export interface StackDatum {
  key: string;
  label: string;
  title: string;
  parts: { key: string; label: string; value: number; color: string }[];
}

export function StackedBars({ data, fmt, labelEvery = 1 }: { data: StackDatum[]; fmt: (v: number) => string; labelEvery?: number }) {
  const [svgRef, k] = useSvgScale(W);   // textos com 12px na tela mesmo com o gráfico encolhido
  const [tip, setTip] = useState<TipState | null>(null);
  const totals = data.map((d) => d.parts.reduce((a, p) => a + p.value, 0));
  const max = Math.max(1, ...totals);
  const ticks = niceTicks(0, max, 4);
  const L = leftPad(k, ticks.map((v) => fmt(v)));
  const plotW = W - L - PAD.r;
  const top = Math.max(max, ticks[ticks.length - 1] ?? max);
  // com a fonte compensada (k), o rótulo da barra mais alta precisa de mais espaço no topo e as datas, de mais espaço
  const padT = PAD.t + Math.round(14 * (k - 1));
  const padB = PAD.b + Math.round(12 * (k - 1));   // o rótulo do eixo X cresce com k: sem isso encosta no "0" do eixo Y
  const plotH = H - padT - padB;
  const sy = (v: number) => padT + plotH - (v / top) * plotH;
  const every = Math.max(labelEvery, Math.ceil((data.length * 40 * k) / plotW));
  const slot = plotW / Math.max(1, data.length);
  const bw = Math.min(24, slot * 0.6);
  return (
    <div className="chart-plot" onPointerLeave={() => setTip(null)}>
      <svg ref={svgRef} style={svgScaleStyle(k)} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("Distribuição de intensidade")}>
        <YGrid left={L} ticks={ticks} sy={sy} fmt={fmt} />
        {data.map((d, i) => {
          const x = L + slot * i + (slot - bw) / 2;
          let acc = 0;
          const visible = d.parts.filter((p) => p.value > 0);
          const show = () =>
            setTip({
              xPct: ((L + slot * i + slot / 2) / W) * 100,
              yPct: (sy(totals[i]) / H) * 100,
              title: d.title,
              lines: [...d.parts].reverse().map((p) => ({ key: p.key, label: p.label, value: fmt(p.value), swatch: p.color })),
            });
          return (
            <g key={d.key}>
              {visible.map((p, j) => {
                const y0 = sy(acc);
                acc += p.value;
                const y1 = sy(acc);
                const isTop = j === visible.length - 1;
                // 2 px de superfície entre segmentos (o vão separa, não um contorno)
                const gap = j > 0 ? 2 : 0;
                return isTop ? (
                  <path key={p.key} d={barPath(x, bw, y1, y0 - gap)} fill={p.color} />
                ) : (
                  <rect key={p.key} x={x} y={y1} width={bw} height={Math.max(0, y0 - y1 - gap)} fill={p.color} />
                );
              })}
              {i % every === 0 && (
                <text x={L + slot * i + slot / 2} y={H - 8} textAnchor="middle" className="tick">
                  {d.label}
                </text>
              )}
              <rect
                x={L + slot * i}
                y={padT}
                width={slot}
                height={plotH}
                className="hit"
                tabIndex={0}
                aria-label={`${d.title}: ${d.parts.map((p) => `${p.label} ${fmt(p.value)}`).join(", ")}`}
                onPointerMove={show}
                onFocus={show}
                onBlur={() => setTip(null)}
              />
            </g>
          );
        })}
        <line x1={L} x2={W - PAD.r} y1={sy(0)} y2={sy(0)} className="axis" />
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
}

// ---------------------------------------------------------------------------- pontos no tempo (ritmo, eficiência, recordes)

export interface TimePoint {
  key: string;
  date: string; // ISO yyyy-mm-dd
  value: number;
  title: string;
  extra?: string[];
}

function dayNum(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
}

/** Meses abreviados no idioma da interface ("jan", "ene", "Jan"…), sem o ponto final de algumas línguas. */
const MONTHS = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat(LOCALE, { month: "short", timeZone: "UTC" }).format(Date.UTC(2000, i, 1)).replace(/\.$/, ""),
);

export function TimeChart({
  points,
  fmt,
  valueLabel,
  invert,
  step,
  from,
  to,
  trend,
}: {
  points: TimePoint[];
  fmt: (v: number) => string;
  valueLabel: string;
  invert?: boolean; // ritmo: menor (mais rápido) em cima
  step?: boolean; // recordes: linha em degraus
  from: string;
  to: string;
  trend?: boolean; // média móvel de 5 pontos como linha
}) {
  const [svgRef, k] = useSvgScale(W);   // textos com 12px na tela mesmo com o gráfico encolhido
  const [tip, setTip] = useState<TipState | null>(null);
  const x0 = dayNum(from);
  const x1 = Math.max(x0 + 1, dayNum(to));
  const vals = points.map((p) => p.value);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const pad = (hi - lo) * 0.12 || Math.max(1, hi * 0.05);
  const ticks = niceTicks(lo - pad, hi + pad, 3);
  const L = leftPad(k, ticks.map((v) => fmt(v)));
  const plotW = W - L - PAD.r;
  const yMin = Math.min(lo - pad, ticks[0] ?? lo);
  const yMax = Math.max(hi + pad, ticks[ticks.length - 1] ?? hi);
  const sx = (iso: string) => L + ((dayNum(iso) - x0) / (x1 - x0)) * plotW;
  const sy = (v: number) => {
    const f = (v - yMin) / (yMax - yMin || 1);
    return invert ? PAD.t + f * PLOT_H : PAD.t + PLOT_H - f * PLOT_H;
  };
  const sorted = useMemo(() => [...points].sort((a, b) => a.date.localeCompare(b.date)), [points]);
  const line = useMemo(() => {
    if (step) {
      return sorted
        .map((p, i) => (i === 0 ? `M${sx(p.date)},${sy(p.value)}` : `H${sx(p.date)}V${sy(p.value)}`))
        .join("") + (sorted.length ? `H${L + plotW}` : "");
    }
    if (!trend || sorted.length < 5) return "";
    const avg = sorted.map((_, i) => {
      const win = sorted.slice(Math.max(0, i - 2), i + 3);
      return win.reduce((a, p) => a + p.value, 0) / win.length;
    });
    return sorted.map((p, i) => `${i ? "L" : "M"}${sx(p.date).toFixed(1)},${sy(avg[i]).toFixed(1)}`).join("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, step, trend, from, to, yMin, yMax]);
  const months: string[] = [];
  {
    const [fy, fm] = from.split("-").map(Number);
    let y = fy;
    let m = fm;
    for (let k = 0; k < 15; k++) {
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
      const iso = `${y}-${String(m).padStart(2, "0")}-01`;
      if (iso > to) break;
      months.push(iso);
    }
  }
  const nearest = (clientXPct: number) => {
    const target = x0 + ((clientXPct * W - L) / plotW) * (x1 - x0);
    let best = 0;
    sorted.forEach((p, i) => {
      if (Math.abs(dayNum(p.date) - target) < Math.abs(dayNum(sorted[best].date) - target)) best = i;
    });
    return best;
  };
  const showIdx = (i: number) => {
    const p = sorted[i];
    if (!p) return;
    setTip({
      xPct: (sx(p.date) / W) * 100,
      yPct: (sy(p.value) / H) * 100,
      title: p.title,
      lines: [{ key: "v", label: valueLabel, value: fmt(p.value), swatch: "var(--chart-1)", line: true }, ...(p.extra ?? []).map((e, j) => ({ key: `e${j}`, label: "", value: e }))],
    });
  };
  const [cross, setCross] = useState<number | null>(null);
  return (
    <div
      className="chart-plot"
      onPointerMove={(e) => {
        const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        const i = nearest((e.clientX - r.left) / r.width);
        setCross(i);
        showIdx(i);
      }}
      onPointerLeave={() => {
        setTip(null);
        setCross(null);
      }}
    >
      <svg ref={svgRef} style={svgScaleStyle(k)} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={valueLabel}>
        <YGrid left={L} ticks={ticks.filter((tp) => tp >= yMin && tp <= yMax)} sy={sy} fmt={fmt} />
        {months.map((m) => (
          <text key={m} x={sx(m)} y={H - 8} textAnchor="middle" className="tick">
            {MONTHS[Number(m.slice(5, 7)) - 1]}
          </text>
        ))}
        {cross != null && sorted[cross] && <line x1={sx(sorted[cross].date)} x2={sx(sorted[cross].date)} y1={PAD.t} y2={PAD.t + PLOT_H} className="crosshair" />}
        {line && <path d={line} className={step ? "series-line" : "trend-line"} />}
        {sorted.map((p, i) => (
          <circle
            key={p.key}
            cx={sx(p.date)}
            cy={sy(p.value)}
            r={cross === i ? 6 : 4.5}
            className="dot"
            tabIndex={0}
            aria-label={`${p.title}: ${fmt(p.value)}`}
            onFocus={() => {
              setCross(i);
              showIdx(i);
            }}
            onBlur={() => {
              setTip(null);
              setCross(null);
            }}
          />
        ))}
        <line x1={L} x2={W - PAD.r} y1={PAD.t + PLOT_H} y2={PAD.t + PLOT_H} className="axis" />
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
}

// ---------------------------------------------------------------------------- barras horizontais (zonas)

export function HBars({ rows, fmt, color = "var(--chart-1)" }: { rows: { key: string; label: string; sub?: string; value: number }[]; fmt: (v: number) => string; color?: string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const total = rows.reduce((a, r) => a + r.value, 0) || 1;
  return (
    <ul className="hbars">
      {rows.map((r) => (
        <li key={r.key}>
          <span className="hbar-label">
            {r.label}
            {r.sub && <small>{r.sub}</small>}
          </span>
          <span className="hbar-track">
            <span className="hbar-fill" style={{ width: `${(r.value / max) * 100}%`, background: color }} />
          </span>
          <span className="hbar-val figs">
            {fmt(r.value)} <small>({Math.round((r.value / total) * 100)} %)</small>
          </span>
        </li>
      ))}
    </ul>
  );
}

export const INTENSITY_LABEL: Record<"low" | "moderate" | "high", string> = { low: t("Baixa"), moderate: t("Moderada"), high: t("Alta") };
export const INTENSITY_COLOR: Record<"low" | "moderate" | "high", string> = {
  low: "var(--int-low)",
  moderate: "var(--int-mod)",
  high: "var(--int-high)",
};
